import { NumericFormat } from 'react-number-format';
import styles from '../../index.module.scss';
import type { EmployeeGroup } from 'components/EmployeeCost/DirectAllocationImport/wizardReducer';
import type { TreeOption } from 'components/EmployeeCost/DirectAllocationImport/directAllocationResolve';
import type { DirectAllocationChat } from '../useDirectAllocationChat';

const BLANK_LABEL = { customer: 'Blank Customer', class: 'Blank Class' } as const;

// Flattens a (possibly nested) QBO customer/class tree into a flat option list
// for a plain <select>, keeping the "Select Customer"/"Select Class" blank
// placeholder (value: '') that directAllocationResolve.ts's flattenTreeOptions
// deliberately drops (it's built for fuzzy-matching, not for a picker UI).
function flattenForSelect(
	options: TreeOption[] | undefined | null,
	kind: 'customer' | 'class'
): Array<{ value: string; title: string }> {
	const out: Array<{ value: string; title: string }> = [];
	const placeholder = kind === 'customer' ? 'Select Customer' : 'Select Class';
	const walk = (nodes: TreeOption[] | undefined | null, depth: number) => {
		(nodes ?? []).forEach((n) => {
			if (n.value !== undefined && n.value !== null && n.title) {
				const title = n.title === placeholder ? BLANK_LABEL[kind] : `${'  '.repeat(depth)}${n.title}`;
				out.push({ value: String(n.value), title });
			}
			if (n.children) walk(n.children, depth + 1);
		});
	};
	walk(options, 0);
	return out;
}

export default function PreviewCard({ chat }: { chat: DirectAllocationChat }) {
	const { session, dispatch } = chat;
	const groups = session.employeeGroups;
	const validationByGroup = new Map(chat.validations.map((v) => [v.groupId, v]));
	const employeeOptions = chat.roster.map((e) => ({ value: e.id, label: e.fullName }));
	const customerFlat = flattenForSelect(chat.customerOptions, 'customer');
	const classFlat = flattenForSelect(chat.classOptions, 'class');

	const reviewCount = groups.filter((g) => g.matchStatus !== 'matched').length;
	const invalidCount = groups.filter((g) => !validationByGroup.get(g.groupId)?.isValid).length;

	const renderGroup = (group: EmployeeGroup) => {
		const validation = validationByGroup.get(group.groupId);
		return (
			<div key={group.groupId} className={styles['import-table-wrap']} style={{ marginBottom: 12 }}>
				<div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
					<strong>{group.matchedEmployeeName ?? group.csvName}</strong>
					<span className={styles['import-table__badge']}>
						{group.matchStatus === 'matched' ? 'Matched' : 'Review'}
					</span>
					{validation && (
						<span className={styles['action-card__status']} style={{ margin: 0 }}>
							Total: {validation.total}%
						</span>
					)}
				</div>
				{group.matchStatus !== 'matched' && (
					<select
						value={group.matchedEmployeeId ?? ''}
						onChange={(e) => {
							const emp = chat.roster.find((r) => r.id === e.target.value);
							dispatch({
								type: 'GROUP_EMPLOYEE_OVERRIDDEN',
								payload: { groupId: group.groupId, employeeId: e.target.value, employeeName: emp?.fullName ?? null },
							});
						}}
						style={{ marginBottom: 6 }}
					>
						<option value="" disabled>
							Select employee…
						</option>
						{employeeOptions.map((o) => (
							<option key={o.value} value={o.value}>
								{o.label}
							</option>
						))}
					</select>
				)}
				<table className={styles['import-table']}>
					<thead>
						<tr>
							<th>Customer</th>
							<th>Class</th>
							<th>% of Hours</th>
						</tr>
					</thead>
					<tbody>
						{group.rows.map((row) => (
							<tr key={row.rowId}>
								<td>
									<select
										value={row.customerId}
										onChange={(e) => {
											const opt = customerFlat.find((o) => o.value === e.target.value);
											dispatch({
												type: 'ROW_CUSTOMER_CHANGED',
												payload: {
													groupId: group.groupId,
													rowId: row.rowId,
													customerId: e.target.value,
													customerName: opt?.title === BLANK_LABEL.customer ? '' : opt?.title ?? '',
												},
											});
										}}
									>
										{customerFlat.map((o) => (
											<option key={o.value} value={o.value}>
												{o.title}
											</option>
										))}
									</select>
								</td>
								<td>
									<select
										value={row.classId}
										onChange={(e) => {
											const opt = classFlat.find((o) => o.value === e.target.value);
											dispatch({
												type: 'ROW_CLASS_CHANGED',
												payload: {
													groupId: group.groupId,
													rowId: row.rowId,
													classId: e.target.value,
													className: opt?.title === BLANK_LABEL.class ? '' : opt?.title ?? '',
												},
											});
										}}
									>
										{classFlat.map((o) => (
											<option key={o.value} value={o.value}>
												{o.title}
											</option>
										))}
									</select>
								</td>
								<td>
									<NumericFormat
										className={styles['smart-import-card__name-input']}
										style={{ width: 80 }}
										value={row.percent ?? ''}
										decimalScale={2}
										suffix="%"
										onValueChange={(values) =>
											dispatch({
												type: 'ROW_PERCENT_CHANGED',
												payload: { groupId: group.groupId, rowId: row.rowId, percent: values.floatValue ?? null },
											})
										}
										isAllowed={(values) => {
											const { floatValue } = values;
											return floatValue === undefined || (floatValue >= 0 && floatValue <= 100);
										}}
									/>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		);
	};

	return (
		<div className={styles['action-card']}>
			<div className={styles['action-card__summary']}>
				{groups.length} employee(s), {groups.reduce((n, g) => n + g.rows.length, 0)} allocation row(s).
			</div>
			{(reviewCount > 0 || invalidCount > 0) && (
				<div className={styles['action-card__status']}>
					{reviewCount > 0 && `${reviewCount} employee(s) need to be matched. `}
					{invalidCount > 0 && `${invalidCount} employee(s) don't total 100%.`}
				</div>
			)}

			{groups.map(renderGroup)}

			{chat.importErrors.length > 0 && (
				<div className={styles['action-card__status']}>
					<strong>Could not import:</strong>
					<ul className={styles['smart-import-card__error-list']}>
						{chat.importErrors.map((e, i) => (
							<li key={i}>{e.message}</li>
						))}
					</ul>
				</div>
			)}
			{chat.generalError && <div className={styles['action-card__status']}>{chat.generalError}</div>}

			<div className={styles['smart-import-card__buttons-wrap']}>
				<button type="button" className={styles['action-card__cancel']} onClick={chat.cancelImport}>
					Cancel
				</button>
				<button
					type="button"
					className={styles['action-card__confirm']}
					disabled={!chat.ready || chat.submitting}
					onClick={() => void chat.confirmImport()}
				>
					{chat.submitting ? 'Importing…' : 'Confirm Import'}
				</button>
			</div>
		</div>
	);
}
